import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { 
  getAllDepartmentalGoals,
  createDepartmentalGoals,
  updateDepartmentalGoalsDetails,
  deleteDepartmentalGoals
} from '../../services/departmentalGoalsService';
import { getCurrentQuarter } from '../../services/goalsService';
import { DepartmentalGoals, DepartmentalGoalItem } from '../../types';
import { 
  Building2,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target
} from 'lucide-react';

const DepartmentalGoalsDashboard = () => {
  const { user } = useAuth();
  const [allGoals, setAllGoals] = useState<DepartmentalGoals[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  
  // Form state
  const [formDepartment, setFormDepartment] = useState('');
  const [formQuarter, setFormQuarter] = useState(1);
  const [formYear, setFormYear] = useState(new Date().getFullYear());
  const [formGoals, setFormGoals] = useState<DepartmentalGoalItem[]>([
    { number: 1, title: '', description: '' }
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { quarter: currentQuarter, year: currentYear } = getCurrentQuarter();

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const goals = await getAllDepartmentalGoals();
      setAllGoals(goals);
    } catch (error) {
      console.error('Error fetching departmental goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormDepartment('');
    setFormQuarter(currentQuarter);
    setFormYear(currentYear);
    setFormGoals([{ number: 1, title: '', description: '' }]);
    setError(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowCreateForm(true);
    setEditingId(null);
  };

  const handleEdit = (deptGoals: DepartmentalGoals) => {
    setFormDepartment(deptGoals.department);
    setFormQuarter(deptGoals.quarter);
    setFormYear(deptGoals.year);
    setFormGoals(deptGoals.goals);
    setEditingId(deptGoals.id || null);
    setShowCreateForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingId(null);
    resetForm();
  };

  const addGoalField = () => {
    if (formGoals.length >= 7) {
      setError('Maximum 7 goals allowed per department');
      return;
    }
    setFormGoals([...formGoals, { number: formGoals.length + 1, title: '', description: '' }]);
  };

  const removeGoalField = (index: number) => {
    if (formGoals.length <= 3) {
      setError('Minimum 3 goals required per department');
      return;
    }
    const newGoals = formGoals.filter((_, i) => i !== index).map((g, i) => ({ ...g, number: i + 1 }));
    setFormGoals(newGoals);
  };

  const updateGoalField = (index: number, field: 'title' | 'description', value: string) => {
    const newGoals = [...formGoals];
    newGoals[index] = { ...newGoals[index], [field]: value };
    setFormGoals(newGoals);
  };

  const validateForm = (): boolean => {
    if (!formDepartment.trim()) {
      setError('Department name is required');
      return false;
    }
    if (formGoals.length < 3) {
      setError('Minimum 3 goals required');
      return false;
    }
    for (let i = 0; i < formGoals.length; i++) {
      if (!formGoals[i].title.trim()) {
        setError(`Goal ${i + 1} needs a title`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;

    setSaving(true);
    setError(null);

    try {
      if (editingId) {
        await updateDepartmentalGoalsDetails(editingId, formDepartment, formQuarter, formYear, formGoals);
      } else {
        await createDepartmentalGoals(formDepartment, formQuarter, formYear, formGoals, user.email);
      }
      await fetchGoals();
      handleCancel();
    } catch (err: any) {
      console.error('Error saving departmental goals:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, department: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete goals for "${department}"?\n\nThis cannot be undone.`);
    if (confirmed) {
      try {
        await deleteDepartmentalGoals(id);
        setAllGoals(prev => prev.filter(g => g.id !== id));
      } catch (error) {
        console.error('Error deleting:', error);
        alert('Failed to delete. Check console for details.');
      }
    }
  };

  const toggleExpanded = (key: string) => {
    const newExpanded = new Set(expandedDepts);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedDepts(newExpanded);
  };

  // Group goals by quarter/year
  const goalsByPeriod = allGoals.reduce((acc, goal) => {
    const key = `Q${goal.quarter} ${goal.year}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(goal);
    return acc;
  }, {} as Record<string, DepartmentalGoals[]>);

  const sortedPeriods = Object.keys(goalsByPeriod).sort((a, b) => {
    const [qA, yA] = a.replace('Q', '').split(' ');
    const [qB, yB] = b.replace('Q', '').split(' ');
    if (yA !== yB) return parseInt(yB) - parseInt(yA);
    return parseInt(qB) - parseInt(qA);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departmental Goals</h1>
          <p className="text-gray-500">Manage goals by department and quarter</p>
        </div>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Department Goals
        </button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingId ? 'Edit Department Goals' : 'Create Department Goals'}
            </h2>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                value={formDepartment}
                onChange={(e) => setFormDepartment(e.target.value)}
                placeholder="e.g., Sales, Marketing, Engineering"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
              <select
                value={formQuarter}
                onChange={(e) => setFormQuarter(Number(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>Q1</option>
                <option value={2}>Q2</option>
                <option value={3}>Q3</option>
                <option value={4}>Q4</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={formYear}
                onChange={(e) => setFormYear(Number(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Goals (3-7)</label>
              <button
                onClick={addGoalField}
                disabled={formGoals.length >= 7}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                + Add Goal
              </button>
            </div>
            
            {formGoals.map((goal, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Goal {index + 1}</span>
                  {formGoals.length > 3 && (
                    <button
                      onClick={() => removeGoalField(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={goal.title}
                    onChange={(e) => updateGoalField(index, 'title', e.target.value)}
                    placeholder="Goal title"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    value={goal.description}
                    onChange={(e) => updateGoalField(index, 'description', e.target.value)}
                    placeholder="Goal description (optional)"
                    rows={2}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Goals List */}
      {sortedPeriods.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No departmental goals created yet</p>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create your first department goals
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedPeriods.map(period => {
            const isExpanded = expandedDepts.has(period);
            const periodGoals = goalsByPeriod[period];
            
            return (
              <div key={period} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleExpanded(period)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{period}</span>
                    <span className="text-sm text-gray-500">
                      ({periodGoals.length} department{periodGoals.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="border-t divide-y">
                    {periodGoals.map(deptGoal => (
                      <div key={deptGoal.id} className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{deptGoal.department}</span>
                            <span className="text-sm text-gray-400">
                              ({deptGoal.goals.length} goals)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(deptGoal)}
                              className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deptGoal.id && handleDelete(deptGoal.id, deptGoal.department)}
                              className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-2 ml-6">
                          {deptGoal.goals.map((goal, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start gap-2">
                                <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="font-medium text-gray-900">{goal.title}</p>
                                  {goal.description && (
                                    <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DepartmentalGoalsDashboard;
